<template>
    <div class="user-profile-container">
        <div class="layout-tabs">
            <v-tabs v-model="activeTab" grow>
                <v-tab>Profile</v-tab>
                <v-tab>Intelligence</v-tab>
            </v-tabs>
            <v-tabs-items v-model="activeTab">
                <v-tab-item>
                    <div class="profile-card profile-card-tab">
                        <div class="profile-header">
                            <div class="save-indicator" v-if="isSaving">
                                <v-progress-circular indeterminate size="14" width="2"
                                    color="primary"></v-progress-circular>
                                <span class="ml-1 caption text--secondary">Saving...</span>
                            </div>
                            <div class="save-indicator" v-else-if="saved && initialized">
                                <v-icon color="green" size="14">mdi-check-circle</v-icon>
                                <span class="ml-1 caption text--secondary">Saved</span>
                            </div>
                        </div>
                        <div class="profile-form-grid">
                            <div class="form-section"><v-text-field v-model="role_title" label="Role"
                                    placeholder="e.g. Sales Manager" outlined dense hide-details class="profile-field"
                                    @input="debouncedSave" /></div>
                            <div class="form-section"><v-combobox v-model="service_chips" :items="[]" label="Services"
                                    placeholder="Type and Enter to add" multiple chips deletable-chips clearable
                                    outlined dense hide-details :search-input.sync="services"
                                    :menu-props="{ maxWidth: '350' }" class="profile-field"
                                    @keydown.enter.prevent="add_service_chip(services)"
                                    @keydown.tab.prevent="add_service_chip(services)" @input="debouncedSave">
                                    <template v-slot:selection="{ attrs, item, select, selected }"><v-chip
                                            v-bind="attrs" :input-value="selected" color="primary" small label close
                                            class="service-chip" @click="select"
                                            @click:close="remove_service_chip(item)">{{ item }}</v-chip></template>
                                    <template v-slot:no-data>
                                        <div class="px-4 py-2 text--secondary caption">Press Enter to add</div>
                                    </template>
                                </v-combobox></div>
                            <div class="form-section"><v-text-field v-model="sales_methodology"
                                    label="Sales methodology" outlined dense hide-details class="profile-field"
                                    @input="debouncedSave" /></div>
                            <div class="form-section"><v-text-field v-model="bid_methodology" label="Bid methodology"
                                    outlined dense hide-details class="profile-field" @input="debouncedSave" /></div>
                            <div class="form-section"><v-text-field v-model="sales_quota_min" label="Quota min"
                                    type="number" outlined dense hide-details class="profile-field"
                                    @input="debouncedSave" /></div>
                            <div class="form-section form-section-last"><v-text-field v-model="sales_quota_max"
                                    label="Quota max" type="number" outlined dense hide-details class="profile-field"
                                    @input="debouncedSave" /></div>
                        </div>
                    </div>
                </v-tab-item>
                <v-tab-item>
                    <div class="cluster-tabs-list cluster-tabs-grid">
                        <div v-for="section in clusterSections" :key="section.key" class="cluster-card">
                            <div class="cluster-card-header"><v-icon v-if="section.iconColor" :color="section.iconColor"
                                    class="mr-2">{{
                                    section.icon }}</v-icon><v-icon v-else :class="section.iconClass" class="mr-2">{{
                                    section.icon
                                    }}</v-icon><span class="card-label">{{ section.label }}</span></div>
                            <p class="cluster-card-desc caption text--secondary">{{ section.desc }}</p>
                            <div v-if="clustersLoading" class="cluster-list-loading py-6"><v-progress-circular
                                    indeterminate color="primary" size="32"></v-progress-circular></div>
                            <div v-else-if="section.clusters.length === 0" class="cluster-list-empty py-4">
                                <v-icon size="40" color="grey lighten-1">{{ section.icon }}</v-icon>
                                <p class="caption text--secondary mt-2 mb-0">{{ section.emptyMessage }}</p>
                                <router-link :to="section.manageLink" class="cluster-link caption mt-1">Manage
                                    clusters</router-link>
                            </div>
                            <div v-else class="cluster-list">
                                <router-link v-for="cluster in section.clusters" :key="cluster.id"
                                    :to="`/cluster/${cluster.id}`" class="cluster-item">
                                    <div class="cluster-item-content">
                                        <div class="cluster-item-row">
                                            <span class="cluster-name">{{ cluster.name }}</span>
                                            <span v-if="section.showEntityTypeTag" class="entity-type-tag"
                                                :class="cluster.entity_type === 'publisher' ? 'entity-type-buyer' : 'entity-type-supplier'">{{
                                                    cluster.entity_type === 'publisher' ? 'Buyer' : 'Supplier' }}</span>
                                            <span class="cluster-count">{{ getClusterEntityCount(cluster) }} {{
                                                cluster.entity_type ===
                                                'publisher' ? 'accounts' : 'suppliers' }}</span>
                                            <v-icon small class="cluster-arrow">mdi-chevron-right</v-icon>
                                        </div>
                                        <div v-if="getClusterEntitySample(cluster.id).length" class="cluster-sample">
                                            <span
                                                v-for="entity in getClusterEntitySample(cluster.id).filter(e => e.name)"
                                                :key="entity.id" class="cluster-entity-tag">{{ entity.name }}</span>
                                        </div>
                                    </div>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </v-tab-item>
            </v-tabs-items>
        </div>

        <!-- Save Notification -->
        <v-snackbar v-model="showSnackbar" :timeout="3000" color="green" top right class="save-snackbar">
            <div class="d-flex align-center">
                <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
                <span>Profile saved successfully!</span>
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="showSnackbar = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import _ from 'lodash';
import { getUserProfile, updateUserProfile, getClusters, fetchPublishers, fetchSuppliers } from "./APIManager.js";

const CLUSTER_TYPE_PARTNERS = 1;
const CLUSTER_TYPE_CUSTOMERS = 2;
const CLUSTER_TYPE_COMPETITORS = 3;
const CLUSTER_TYPE_SUPPLIERS = 5;
const CLUSTER_TYPE_TARGET_ACCOUNTS = 6;

export default {
    components: {
    },
    props: {
        source: String
    },
    data() {
        return {
            role_title: "",
            sales_methodology: "",
            bid_methodology: "",
            service_chips: [],
            services: "",
            sales_quota_min: null,
            sales_quota_max: null,

            isLoading: false,
            initialized: false,
            isSaving: false,
            saved: false,
            showSnackbar: false,

            clustersLoading: false,
            partnersClusters: [],
            competitorsClusters: [],
            suppliersClusters: [],
            customersClusters: [],
            customersClustersSuppliers: [],
            targetAccountsClusters: [],
            targetAccountsClustersSuppliers: [],
            clusterEntitySamples: {},  // clusterId -> array of { id, name }
            activeTab: 0
        };
    },
    computed: {
        clusterSections() {
            return [
                { key: 'partners', label: 'Partners', icon: 'mdi-handshake', iconColor: 'primary', clusters: this.partnersClusters, entityLabel: 'suppliers', manageLink: '/clusters?entity_type=supplier', desc: 'Supplier clusters you\'ve marked as partners.', emptyMessage: 'No partner clusters yet', showEntityTypeTag: false },
                { key: 'competitors', label: 'Competitors', icon: 'mdi-account-group', iconClass: 'competitors-icon', clusters: this.competitorsClusters, entityLabel: 'suppliers', manageLink: '/clusters?entity_type=supplier', desc: 'Supplier clusters you\'re tracking as competitors.', emptyMessage: 'No competitor clusters yet', showEntityTypeTag: false },
                { key: 'customers', label: 'Customers', icon: 'mdi-domain', iconClass: 'customers-icon', clusters: [...this.customersClusters, ...this.customersClustersSuppliers], entityLabel: 'accounts', manageLink: '/clusters', desc: 'Buyer or supplier clusters you\'ve mapped as customers.', emptyMessage: 'No customer clusters yet', showEntityTypeTag: true },
                { key: 'targets', label: 'Target Accounts', icon: 'mdi-bullseye-arrow', iconClass: 'target-accounts-icon', clusters: [...this.targetAccountsClusters, ...this.targetAccountsClustersSuppliers], entityLabel: 'accounts', manageLink: '/clusters', desc: 'Buyer or supplier clusters you\'re targeting.', emptyMessage: 'No target account clusters yet', showEntityTypeTag: true },
                { key: 'suppliers', label: 'Suppliers', icon: 'mdi-package-variant', iconClass: 'suppliers-icon', clusters: this.suppliersClusters, entityLabel: 'suppliers', manageLink: '/clusters?entity_type=supplier', desc: 'Supplier clusters you\'re tracking.', emptyMessage: 'No supplier clusters yet', showEntityTypeTag: false },
            ];
        }
    },
    watch: {
        // Persist keyword changes. The Services combobox mutates service_chips
        // through custom add/remove handlers (not the combobox's own @input),
        // so without this watcher a keyword edit updates the UI but never
        // triggers a save. Guarded by `initialized` so the initial load that
        // sets service_chips doesn't fire a spurious save.
        service_chips() {
            if (this.initialized) this.debouncedSave();
        },
    },
    mounted() {
        // Load user profile data
        getUserProfile().then(results => {
            this.role_title = results.role_title;
            this.service_chips = results.services;
            this.sales_methodology = results.sales_methodology;
            this.bid_methodology = results.bid_methodology;
            this.sales_quota_min = results.sales_quota_min;
            this.sales_quota_max = results.sales_quota_max;

            this.initialized = true;
        }).catch(error => {
            console.error('Error loading user profile:', error);
        });

        this.loadClusters();
    },
    methods: {

        add_service_chip(value) {
            if (value && !this.service_chips.includes(value)) {
                this.service_chips.push(value);
                this.services = ""; // Clear input after adding
            }
        },
        remove_service_chip(item) {
            this.service_chips.splice(
                this.service_chips.indexOf(item),
                1
            );
            this.service_chips = [...this.service_chips];
        },

        async save() {
            if (!this.initialized) return;

            this.isSaving = true;
            this.saved = false;

            try {
                await updateUserProfile(this.getProfileData());
                this.saved = true;
                this.showSnackbar = true;

                // Reset saved indicator after 3 seconds
                setTimeout(() => {
                    this.saved = false;
                }, 3000);
            } catch (error) {
                console.error('Error saving profile:', error);
                // Could add error notification here
            } finally {
                this.isSaving = false;
            }
        },

        getProfileData() {
            return {
                role_title: this.role_title,
                sales_methodology: this.sales_methodology,
                bid_methodology: this.bid_methodology,
                sales_quota_min: this.sales_quota_min,
                sales_quota_max: this.sales_quota_max,
                services: this.service_chips,
            };
        },

        debouncedSave: _.debounce(function () {
            if (this.initialized) {
                this.save();
            }
        }, 1000),

        async loadClusters() {
            this.clustersLoading = true;
            try {
                const [supplierRes, publisherRes] = await Promise.all([
                    getClusters('supplier'),
                    getClusters('publisher')
                ]);
                const parseQuery = (c) => {
                    if (c.search_query && typeof c.search_query === 'string') {
                        try {
                            c.search_query = JSON.parse(c.search_query);
                        } catch (e) {
                            c.search_query = {};
                        }
                    }
                    if (!c.search_query) c.search_query = {};
                };
                const supplierList = supplierRes.data || [];
                supplierList.forEach(c => {
                    parseQuery(c);
                    c.entity_type = 'supplier';
                });
                const publisherList = publisherRes.data || [];
                publisherList.forEach(c => {
                    parseQuery(c);
                    c.entity_type = 'publisher';
                });
                this.partnersClusters = supplierList.filter(c => c.cluster_type_id === CLUSTER_TYPE_PARTNERS);
                this.competitorsClusters = supplierList.filter(c => c.cluster_type_id === CLUSTER_TYPE_COMPETITORS);
                this.suppliersClusters = supplierList.filter(c => c.cluster_type_id === CLUSTER_TYPE_SUPPLIERS);
                this.customersClusters = publisherList.filter(c => c.cluster_type_id === CLUSTER_TYPE_CUSTOMERS);
                this.customersClustersSuppliers = supplierList.filter(c => c.cluster_type_id === CLUSTER_TYPE_CUSTOMERS);
                this.targetAccountsClusters = publisherList.filter(c => c.cluster_type_id === CLUSTER_TYPE_TARGET_ACCOUNTS);
                this.targetAccountsClustersSuppliers = supplierList.filter(c => c.cluster_type_id === CLUSTER_TYPE_TARGET_ACCOUNTS);
                this.loadClusterEntitySamples();
            } catch (error) {
                console.error('Error loading clusters:', error);
            } finally {
                this.clustersLoading = false;
            }
        },

        async loadClusterEntitySamples() {
            const SAMPLE_SIZE = 5;
            const allClusters = [
                ...this.partnersClusters,
                ...this.competitorsClusters,
                ...this.suppliersClusters,
                ...this.customersClusters,
                ...this.customersClustersSuppliers,
                ...this.targetAccountsClusters,
                ...this.targetAccountsClustersSuppliers
            ];
            const promises = allClusters.map(async (cluster) => {
                const ids = this.getClusterEntityIds(cluster, SAMPLE_SIZE);
                if (ids.length === 0) {
                    this.$set(this.clusterEntitySamples, cluster.id, []);
                    return;
                }
                try {
                    if (cluster.entity_type === 'publisher') {
                        const res = await fetchPublishers(1, { publisher_id: ids });
                        const data = (res && res.data) ? res.data : [];
                        this.$set(this.clusterEntitySamples, cluster.id, data.slice(0, SAMPLE_SIZE).map(e => ({ id: e.id, name: e.name || '' })));
                    } else {
                        const res = await fetchSuppliers(1, { supplier_id: ids });
                        const data = (res && res.data) ? res.data : [];
                        this.$set(this.clusterEntitySamples, cluster.id, data.slice(0, SAMPLE_SIZE).map(e => ({ id: e.id, name: e.name || '' })));
                    }
                } catch (err) {
                    console.error('Error loading cluster entity sample:', cluster.id, err);
                    this.$set(this.clusterEntitySamples, cluster.id, []);
                }
            });
            await Promise.all(promises);
        },

        getClusterEntityIds(cluster, limit = 5) {
            if (!cluster || !cluster.search_query || typeof cluster.search_query !== 'object') return [];
            const q = cluster.search_query;
            let ids = [];
            if (cluster.entity_type === 'publisher') {
                ids = q.publisher_id || q.buyer_id || [];
            } else {
                ids = q.supplier_id || [];
            }
            if (!Array.isArray(ids)) return [];
            return ids.filter(id => !!id).slice(0, limit);
        },

        getClusterEntitySample(clusterId) {
            return this.clusterEntitySamples[clusterId] || [];
        },

        getClusterEntityCount(cluster) {
            if (!cluster || !cluster.search_query || typeof cluster.search_query !== 'object') return 0;
            const q = cluster.search_query;
            if (cluster.entity_type === 'publisher') {
                const ids = q.publisher_id || q.buyer_id || [];
                return Array.isArray(ids) ? ids.filter(id => !!id).length : 0;
            }
            const ids = q.supplier_id || [];
            return Array.isArray(ids) ? ids.filter(id => !!id).length : 0;
        }
    }
};
</script>
<style scoped>
.user-profile-container {
    padding: 20px;
}

.layout-tabs {
    width: 100%;
    max-width: 100%;
}

.layout-tabs ::v-deep .v-tabs {
    border-bottom: 1px solid #e0e0e0;
}

.layout-tabs ::v-deep .v-tab {
    text-transform: none;
    font-size: 14px;
}

.layout-tabs ::v-deep .v-tabs-items {
    background: transparent;
}

.layout-tabs ::v-deep .v-tab-item {
    padding: 0;
}

.profile-card-tab {
    margin-top: 0;
}

.profile-form-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px 20px;
}

@media (min-width: 380px) {
    .profile-form-grid {
        grid-template-columns: repeat(2, 1fr);
    }

}

.cluster-tabs-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 16px;
}

.cluster-tabs-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

@media (min-width: 380px) {
    .cluster-tabs-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.profile-card {
    padding: 16px 20px;
    background: #f4f4f4;
    border-radius: 8px;
}

.profile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e0e0e0;
}

.profile-card-title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.87);
}

.save-indicator {
    display: flex;
    align-items: center;
    font-size: 11px;
}

.form-section {
    margin-bottom: 12px;
}

.form-section-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.form-section-last {
    margin-bottom: 0;
}

.profile-field {
    margin-top: 0;
}

.service-chip {
    margin: 2px 4px 2px 0;
    font-weight: 500;
}

.card-label {
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.87);
}

/* Cluster cards */
.cluster-card {
    padding: 14px 16px;
    background: #f4f4f4;
    border-radius: 8px;
}

.cluster-card-header {
    display: flex;
    align-items: center;
    margin-bottom: 2px;
}

.cluster-card-header .card-label {
    font-size: 14px;
}

.competitors-icon {
    color: #d32f2f;
}

.suppliers-icon {
    color: #607d8b;
}

.customers-icon {
    color: #2e7d32;
}

.target-accounts-icon {
    color: #ed6c02;
}

.cluster-card-desc {
    margin-bottom: 8px;
    line-height: 1.35;
    font-size: 12px;
}

.cluster-list-loading,
.cluster-list-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.cluster-link {
    color: #ff1463;
    text-decoration: none;
}

.cluster-link:hover {
    text-decoration: underline;
}

.cluster-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.cluster-item {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    border-radius: 0;
    border: none;
    text-decoration: none;
    color: inherit;
    transition: background-color 0.15s ease;
}

.cluster-item:hover {
    background-color: #f5f5f5;
}

.cluster-item-content {
    flex: 1;
    min-width: 0;
}

.cluster-item-row {
    display: flex;
    align-items: center;
}

.cluster-name {
    flex: 1;
    font-weight: 500;
    font-size: 14px;
    min-width: 0;
}

.cluster-count {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.6);
    margin-right: 8px;
    flex-shrink: 0;
}

.cluster-arrow {
    color: rgba(0, 0, 0, 0.38);
    flex-shrink: 0;
}

.entity-type-tag {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    padding: 2px 6px;
    border-radius: 4px;
    flex-shrink: 0;
    margin-right: 8px;
}

.entity-type-buyer {
    background: rgba(46, 125, 50, 0.12);
    color: #2e7d32;
}

.entity-type-supplier {
    background: rgba(25, 118, 210, 0.12);
    color: #1976d2;
}

.cluster-sample {
    margin-top: 6px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 6px;
    align-items: center;
}

.cluster-entity-tag {
    display: inline-block;
    font-size: 11px;
    line-height: 1.2;
    color: rgba(0, 0, 0, 0.7);
    background-color: #ececec;
    border: none;
    border-radius: 0;
    padding: 2px 6px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Responsive */
@media (max-width: 600px) {
    .profile-card {
        padding: 12px 14px;
    }

    .form-section-row {
        grid-template-columns: 1fr;
        gap: 0;
    }

    .cluster-card {
        padding: 12px 14px;
    }
}

.form-section {
    transition: opacity 0.2s ease;
}

.save-indicator {
    transition: opacity 0.3s ease;
}

::v-deep .v-input--is-focused .v-input__slot {
    border-color: #ff1463 !important;
}

::v-deep .v-text-field--outlined .v-input__slot {
    min-height: 40px;
    border-radius: 0;
}

::v-deep .v-text-field--outlined fieldset,
::v-deep .v-input--is-label-active fieldset {
    border-radius: 0;
}

::v-deep .v-combobox__selection {
    margin-top: 4px;
}

.save-snackbar {
    z-index: 9999;
}
</style>